import React, { useCallback, useMemo } from 'react';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';
import { QlfConfirmationDialogProps, useAlerts } from '@qualifio/ui-commons';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';
import { useTranslation } from 'react-i18next';
import { BlockAndUnblockMemberButtonsProps } from './BlockAndUnblockMemberButtons';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';

export interface UnblockMemberButtonsProps extends BlockAndUnblockMemberButtonsProps {
  onUnblock?: () => Promise<void> | void;
  onButtonClick: () => void;
  confirmationDialogState: QlfConfirmationDialogProps;
  setConfirmationDialogState: (props: QlfConfirmationDialogProps) => void;
}

export function UnblockMemberButton({
  profile,
  programId,
  onUnblock,
  onButtonClick,
  confirmationDialogState,
  setConfirmationDialogState,
}: UnblockMemberButtonsProps) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { features } = useFeatures();
  const { enabled: blockingEnabled } = features[Feature.BLOCKING];

  const { profileApi } = useApi();

  const displayName = useMemo(() => `${profile?.firstName} ${profile?.lastName}`, [profile]);

  const blockMember = useCallback(async () => {
    try {
      await profileApi.unblockMember(programId, profile.id);
      notify(t('profiles.actions.unblock.success', { member: displayName }), 'success');
      await onUnblock?.();
    } catch (error) {
      console.error(error);
      notify(t('generic.error', { error }), 'error');
    }
  }, [profile, programId, onUnblock, displayName]);

  const showDialog = () => {
    setConfirmationDialogState({
      ...confirmationDialogState,
      open: true,
      color: 'primary',
      title: t('profiles.actions.unblock.confirm.title', { member: displayName }),
      content: t('profiles.actions.unblock.confirm.message'),
      confirmButtonColor: 'error',
      confirmButtonText: t('profiles.actions.unblock.confirm.button'),
      cancelButtonText: t('publish.confirm.button.cancel'),
      onConfirm: blockMember,
    });
    onButtonClick();
  };

  return (
    <Tooltip title={blockingEnabled ? '' : t('shared.action.permission.required')}>
      <div>
        <MenuItem onClick={showDialog}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faBan} />
          </ListItemIcon>
          <ListItemText>{t('profiles.actions.unblock.button')}</ListItemText>
        </MenuItem>
      </div>
    </Tooltip>
  );
}
